import { EvidenceChip } from "./EvidenceChip";
import { GRANULARITY } from "./useSciPreview";

export function Section({ id, title, kicker, children, empty }) {
  return (
    <section id={id} data-testid={`sci-section-${id}`} className="scroll-mt-28 border-t border-stone-200 py-10">
      <div className="grid gap-6 lg:grid-cols-[220px,1fr]">
        <div>
          {kicker && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{kicker}</p>}
          <h2 className="font-editorial text-2xl text-stone-900">{title}</h2>
        </div>
        <div className="min-w-0">{empty ? <NotVerified testId={`sci-section-${id}-empty`} /> : children}</div>
      </div>
    </section>
  );
}

export const NotVerified = ({ testId, text = "Not yet verified on an official source, so we're not showing it." }) => (
  <p data-testid={testId} className="text-sm italic text-stone-500">{text}</p>
);

// A verified statement: the official wording (or verbatim value) plus its source chip.
const LABEL = {
  entry_year: "Entry year", typical_offer_a_level: "Typical A-level offer", minimum_offer_a_level: "Minimum A-level offer",
  ib_offer: "IB", required_subjects: "Subjects", admissions_test: "Admissions test", interview: "Interview",
  application_deadline: "Application deadline", admission_requirement: "Requirement", award: "Award", duration: "Duration",
  ucas_code: "UCAS code", fee: "Tuition fee", workload: "Workload",
};

export function ClaimRow({ c, testId, showContext = true }) {
  return (
    <li data-testid={testId} className="flex items-start justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-[15px] leading-relaxed text-stone-800">
          {LABEL[c.field] && <span className="mr-2 text-stone-500">{LABEL[c.field]}:</span>}{c.value}
        </p>
        {showContext && c.context && <p className="font-mono text-[10px] uppercase tracking-wider text-stone-500">{c.context}</p>}
        {c.granularity && c.granularity !== "course" && (
          <p className="text-[11px] text-amber-800">Applies to: {GRANULARITY[c.granularity] || c.granularity}</p>
        )}
      </div>
      <EvidenceChip claim={c} testId={`${testId}-source`} />
    </li>
  );
}

export function ClaimList({ items, prefix }) {
  return (
    <ul className="divide-y divide-stone-100">
      {items.map((c, i) => <ClaimRow key={i} c={c} testId={`${prefix}-${i}`} />)}
    </ul>
  );
}

// Module names grouped by the year/stage given on the page.
export function ModuleGroups({ items, prefix }) {
  const groups = items.reduce((acc, c) => {
    const k = c.context || c.entry_year || "Any year";
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});
  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([k, rows], gi) => (
        <div key={k}>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">{k}</p>
          <div className="flex flex-wrap gap-2">
            {rows.map((c, i) => (
              <span key={i} data-testid={`${prefix}-${gi}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white py-1 pl-3 pr-1 text-sm text-stone-800">
                {c.value}<EvidenceChip claim={c} testId={`${prefix}-${gi}-${i}-source`} />
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
